import { API_BASE } from '@/api/http';
import { routeTemplate } from './routeTemplate';
import { scrubText } from './scrub';

/**
 * The client error beacon (ADR 0067).
 *
 * A self-hosted mail client has no third-party error service, and the owner
 * cannot open devtools on a phone that went blank. So the app POSTs a small,
 * scrubbed record of each distinct error to its own backend, which logs it.
 *
 * What it never sends: a message body, subject or address, a cookie, a token,
 * anything from storage, or the real URL -- only the route TEMPLATE. The text
 * it does send goes through `scrubText` first, and the server scrubs again.
 *
 * It is a raw `fetch`, not the app's API transport: the transport's session,
 * proof and outage handling must not react to a beacon, and the beacon has to
 * work in mock mode, where the transport never touches the network. Every
 * failure is swallowed. A reporter that throws, logs or reports itself turns
 * one error into a loop.
 */

export type ClientErrorKind = 'error' | 'unhandledrejection' | 'boundary' | 'chunk_load';

export interface ClientErrorInput {
  kind: ClientErrorKind;
  message: string;
  stack?: string | undefined;
}

/** The wire body. Exactly these keys: the server decodes with DisallowUnknownFields. */
export interface ClientErrorReport {
  kind: ClientErrorKind;
  source: 'window';
  message: string;
  stack: string;
  route: string;
  app_version: string;
  user_agent: string;
  timestamp: string;
}

export const CLIENT_ERRORS_URL = `${API_BASE}/client-errors`;

const MAX_MESSAGE = 1000;
const MAX_STACK = 4000;
const MAX_ROUTE = 200;
const MAX_VERSION = 64;
const MAX_USER_AGENT = 300;
/** The server refuses a larger body with 413. */
export const MAX_BODY_BYTES = 8 * 1024;

const WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 5;
const MAX_PER_PAGE = 30;

/**
 * Which frontend build this page is running: the content-hashed name of its
 * own entry script, e.g. `index-DkeBm5H7`, read from the document at report
 * time. `dev` under the dev server and in tests.
 *
 * NOT the commit, deliberately. The commit used to be compiled in
 * (`VITE_APP_VERSION`), and that put it in a precached chunk: every deploy --
 * a backend-only one included -- then changed a chunk, the precache manifest
 * and so `sw.js`, and every installed app was asked to reload for a version
 * with no visible change. The owner reported being "continually prompted to
 * reload the app on mobile". The hash changes exactly when the frontend
 * does, which is also the more useful answer for "what code threw this" (a
 * stale tab is running an old build under a new server). The server adds its
 * own commit to the log line as `server_version`.
 *
 * Nothing that varies per deploy but not per source may go into the bundle;
 * `src/pwa/buildStability.test.ts` holds that line.
 */
const ENTRY_SCRIPT = /\/assets\/([A-Za-z0-9_-]{1,64})\.js(?:[?#]|$)/;

export function runningBuild(doc: Document | undefined = globalThis.document): string {
  try {
    if (doc === undefined) return 'dev';
    for (const script of doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
      const match = ENTRY_SCRIPT.exec(script.src);
      if (match?.[1] !== undefined) return match[1].slice(0, MAX_VERSION);
    }
  } catch (_error) {
    // Fall through: a version is a nicety, never a reason to lose the report.
  }
  return 'dev';
}

/**
 * A stale tab asking for a chunk the last deploy deleted. Worth its own kind:
 * it is not a bug in the code, it is a deploy the tab has not noticed.
 */
const CHUNK_LOAD = new RegExp(
  [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'error loading dynamically imported module',
    'Loading chunk .* failed',
    'Unable to preload CSS',
  ].join('|'),
  'i',
);

let seen = new Set<string>();
let sentAt: number[] = [];
let sentThisPage = 0;
let reporting = false;
let installed = false;

/** `Name: message` for an Error, a safe string for anything else. */
export function describeError(value: unknown): { message: string; stack: string } {
  try {
    if (value instanceof Error) {
      const message = value.message ? `${value.name}: ${value.message}` : value.name;
      return { message, stack: typeof value.stack === 'string' ? value.stack : '' };
    }
    return { message: stringifySafely(value), stack: '' };
  } catch (_error) {
    return { message: 'Unserializable error', stack: '' };
  }
}

function stringifySafely(value: unknown): string {
  if (typeof value === 'string') return value.slice(0, MAX_MESSAGE);
  if (value === undefined) return 'undefined';
  try {
    const json = JSON.stringify(value);
    if (typeof json === 'string') return json.slice(0, MAX_MESSAGE);
  } catch (_error) {
    // Circular, BigInt, a throwing toJSON: fall through to String().
  }
  try {
    return String(value).slice(0, MAX_MESSAGE);
  } catch (_error) {
    return 'Unserializable rejection';
  }
}

function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * The body, cut to fit: the stack gives way first, then the message. JSON
 * escaping and multi-byte characters mean a character cap alone cannot promise
 * a byte cap, so this measures.
 */
export function serializeReport(report: ClientErrorReport): string {
  let body = JSON.stringify(report);
  let excess = byteLength(body) - MAX_BODY_BYTES;
  while (excess > 0 && report.stack.length > 0) {
    report.stack = report.stack.slice(0, Math.max(0, report.stack.length - Math.max(excess, 64)));
    body = JSON.stringify(report);
    excess = byteLength(body) - MAX_BODY_BYTES;
  }
  while (excess > 0 && report.message.length > 0) {
    report.message = report.message.slice(0, Math.max(0, report.message.length - Math.max(excess, 64)));
    body = JSON.stringify(report);
    excess = byteLength(body) - MAX_BODY_BYTES;
  }
  return body;
}

function currentRoute(): string {
  try {
    return routeTemplate(window.location.pathname).slice(0, MAX_ROUTE);
  } catch (_error) {
    return '/*';
  }
}

function userAgent(): string {
  try {
    return (navigator.userAgent || '').slice(0, MAX_USER_AGENT);
  } catch (_error) {
    return '';
  }
}

/** Whether another report fits under the per-minute and per-page caps. */
function underCaps(now: number): boolean {
  if (sentThisPage >= MAX_PER_PAGE) return false;
  sentAt = sentAt.filter((at) => now - at < WINDOW_MS);
  return sentAt.length < MAX_PER_WINDOW;
}

/**
 * Report one error. Never throws, never logs; sends each distinct error at
 * most once per page load, and at most 5 a minute and 30 a page.
 */
export function reportClientError(input: ClientErrorInput): void {
  if (reporting) return;
  reporting = true;
  try {
    const rawMessage = String(input.message ?? '');
    const kind: ClientErrorKind = CHUNK_LOAD.test(rawMessage) ? 'chunk_load' : input.kind;
    const message = scrubText(rawMessage, MAX_MESSAGE);
    const stack = scrubText(String(input.stack ?? ''), MAX_STACK);

    const key = `${message}\n${stack}`;
    if (seen.has(key)) return;
    const now = Date.now();
    if (!underCaps(now)) return;
    seen.add(key);
    sentAt.push(now);
    sentThisPage += 1;

    const body = serializeReport({
      kind,
      source: 'window',
      message,
      stack,
      route: currentRoute(),
      app_version: runningBuild(),
      user_agent: userAgent(),
      timestamp: new Date(now).toISOString(),
    });

    const sent = fetch(CLIENT_ERRORS_URL, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    // The answer does not matter, and neither does a failure to get one.
    Promise.resolve(sent).catch(() => undefined);
  } catch (_error) {
    // Swallowed: the reporter must never become a source of errors.
  } finally {
    reporting = false;
  }
}

function onWindowError(event: ErrorEvent): void {
  const error: unknown = event.error;
  const eventMessage = typeof event.message === 'string' ? event.message : '';
  // A failed <img>/<script> load dispatches a plain Event: nothing to say.
  if (!error && !eventMessage) return;
  const described = error ? describeError(error) : { message: eventMessage, stack: '' };
  // A cross-origin script's error, masked by the browser: no detail at all.
  if (/^Script error\.?$/.test(described.message.trim()) && !described.stack) return;
  reportClientError({ kind: 'error', ...described });
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  const described = describeError(event.reason);
  reportClientError({ kind: 'unhandledrejection', ...described });
}

/** Vite's own signal that a lazy chunk (or its CSS) failed to load. */
function onPreloadError(event: Event): void {
  const payload = (event as Event & { payload?: unknown }).payload;
  const described = describeError(payload ?? 'vite:preloadError');
  // Do not preventDefault(): the import still rejects, and the route's error
  // boundary still offers the Reload that fixes it.
  reportClientError({ kind: 'chunk_load', ...described });
}

/**
 * The diagnostic kinds (ADR 0067, amended 2026-09-27): not errors, but a
 * record of the PWA update handover going wrong, with a structured `details`
 * object the server validates field by field. The caller decides when one is
 * worth sending and rate-limits it (`@/pwa/handoverDiagnostics`); this only
 * builds the body and sends it.
 */
export type DiagnosticKind = 'pwa_handover_timeout' | 'pwa_update_loop';

/**
 * Send one diagnostic. Never throws. Sent with `keepalive`, so a reload
 * started right after this call does not cancel it. Returns whether a request
 * was started.
 *
 * `details` must hold nothing personal -- worker states, booleans, counts,
 * the worker script's path -- and match the server's closed shape exactly
 * (`backend/internal/httpapi/pwa_diagnostics.go`): an unknown key is a 400.
 */
export function reportDiagnostic(kind: DiagnosticKind, message: string, details: object): boolean {
  try {
    const body = JSON.stringify({
      kind,
      source: 'window',
      message: message.slice(0, MAX_MESSAGE),
      stack: '',
      route: currentRoute(),
      app_version: runningBuild(),
      user_agent: userAgent(),
      timestamp: new Date().toISOString(),
      details,
    });
    if (byteLength(body) > MAX_BODY_BYTES) return false;
    const sent = fetch(CLIENT_ERRORS_URL, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    Promise.resolve(sent).catch(() => undefined);
    return true;
  } catch (_error) {
    return false;
  }
}

/** Listen for uncaught errors, rejections and chunk failures. Idempotent. */
export function installErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  window.addEventListener('vite:preloadError', onPreloadError);
}

/** Forget every report and remove the listeners. Tests only. */
export function resetForTests(): void {
  seen = new Set();
  sentAt = [];
  sentThisPage = 0;
  reporting = false;
  if (installed) {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    window.removeEventListener('vite:preloadError', onPreloadError);
    installed = false;
  }
}
